import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Icon, Spinner, ErrorState } from "@common/index";
import { ROUTES } from "@routing/routes";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { useShoppingCartApi } from "@/hooks/apiHooks/useShoppingCartApi";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { useOrderApi } from "@/hooks/apiHooks/useOrderApi";
import { useShippingApi } from "@/hooks/apiHooks/useShippingApi";
import { usePaymentQuoteApi } from "@/hooks/apiHooks/usePaymentQuoteApi";
import { useMountRead } from "@/hooks/useMountRead";
import { isCardPaymentConfigured } from "@/config/paymentConfig";
import { PaymentMethodType } from "@models/paymentMethod";
import type { SurchargeQuoteResponse } from "@models/paymentQuote";
import {
    isProcessingShoppingCartAtom,
    shoppingCartAtom,
} from "@/store/shoppingCartAtoms";
import { shippingPolicyAtom } from "@/store/shippingAtoms";
import { locationsAtom } from "@/store/locationAtoms";
import {
    activeShipToLocationAtom,
    browsesUnfilteredAtom,
    eligibleShipToLocationsAtom,
    hasNoLocationMembershipAtom,
    storedShipToLocationIdAtom,
} from "@/store/shipToAtoms";
import { isProcessingOrderAtom } from "@/store/orderAtoms";
import { getProductImage } from "@utils/productImage";
import { NoLocationNote } from "@customer/_components/NoLocationNote";
import {
    NO_LOCATION_CART_NOTE,
    NO_LOCATION_ORDER_BLOCKED,
} from "@customer/_helpers/noLocationCopy";
import { CartItemRow } from "../Cart/CartItemRow";
import { LineStockNotice } from "../Cart/_components/LineStockNotice";
import { cartStock } from "../Cart/_helpers/lineStock";
import {
    lineHasNoOffers,
    noOfferLineCount,
    offeredLines,
} from "../Cart/_helpers/lineOffers";
import {
    outOfStockReason,
    overStockReason,
    stockLimitMessage,
} from "../_helpers/stockCopy";
import { quantityCap } from "../_helpers/quantityCap";
import { useCartProducts } from "../_helpers/useCartProducts";
import { linePiecesPerUnit } from "../_helpers/linePiecesPerUnit";
import { useCartItemMutations } from "../Cart/_helpers/useCartItemMutations";
import { RestrictedLinesNotice } from "../_components/RestrictedLinesNotice";
import {
    CHECKOUT_UNAVAILABLE_MESSAGE,
    CHECKOUT_UNAVAILABLE_REASON,
    CHECKOUT_UNAVAILABLE_TITLE,
} from "../_helpers/checkoutUnavailableCopy";
import {
    restrictedLineCount,
    shippableSubtotal,
    shippableUnitCount,
} from "../_helpers/cartTotals";
import { CheckoutSteps } from "./CheckoutSteps";
import { ShippingCard } from "./ShippingCard";
import { LocationSelectModal } from "./LocationSelectModal";
import {
    PaymentForm,
    type PaymentFormHandle,
    type PaymentSelection,
} from "./PaymentForm";
import { CheckoutSummary } from "./CheckoutSummary";
import { isLocationApproved } from "./_helpers/isLocationApproved";
import {
    SHIPPING_LOCATION_GAP_COPY,
    type ShippingLocationGap,
} from "./_helpers/shippingLocationCopy";
import {
    checkoutOrderError,
    isDoNotRetry,
    withoutShortfallLine,
    type CheckoutOrderError,
    type CheckoutOrderErrorKind,
} from "./_helpers/checkoutOrderError";

// Lifecycle of the #297 card-fee quote. "failed" is deliberately its own state rather than
// an absent quote: §1.11 forbids placing a card order whose fee was never disclosed, so a
// failure still gates Place Order — but it must say so honestly and offer a way forward
// (Retry, or switching to ACH, which carries no fee at all).
type SurchargeQuoteState =
    | { status: "idle" }
    | { status: "loading" }
    | { status: "ready"; quote: SurchargeQuoteResponse }
    | { status: "failed" };

// Not applicable: ACH, an unconfigured environment, an empty cart, or nothing to charge a
// fee on. No request, no gate.
const IDLE_QUOTE: SurchargeQuoteState = { status: "idle" };

// Title line of the inline failure alert, per kind (#156, #441, #443, PR 339).
const CHECKOUT_ERROR_TITLES: Record<CheckoutOrderErrorKind, string> = {
    declined: "Payment declined",
    unavailable: "Payment service unavailable",
    unconfirmed: "Payment not confirmed",
    held: "Payment under review",
    orderSaveFailed: "Order not completed",
    conflict: "Checkout already in progress",
    stockChanged: "Stock changed",
    outOfStock: "Not enough stock",
};

// After the stock-changed 409 the page reads the cart and its stock again (PR 339). When
// that read fails the page still shows the old figures, and says so.
const STALE_STOCK_NOTE =
    "We couldn't load the latest stock, so what's shown may be out of date.";

// After the do-not-retry kinds the server keeps the cart's checkout claim (#441): the lines
// are gone, so a retry finds an empty cart instead of charging again. Said in the alert,
// because the page empties with it.
const CART_EMPTIED_NOTE =
    "Your cart has been emptied so this payment can't be taken twice.";

export const Checkout = () => {
    const navigate = useNavigate();
    const { getShoppingCart } = useShoppingCartApi();
    // The same persisted, debounced quantity edit the Cart page uses:
    // CreateOrder reads the SERVER-side cart, so a quantity changed here has
    // to reach it before Place Order, or the order ships the old number —
    // hasPendingEdits (armed or on the wire) and the cart's processing atom
    // (any cart mutation in flight) both gate Place Order below.
    const { changeQuantity, hasPendingEdits, discardPending, quantityRefusals } =
        useCartItemMutations({
            // #443: once the shopper fixes a line the stock 412 marked, here, that
            // line is unmarked; the alert re-names what is left, and goes with the
            // last. Every other alert stays.
            onEditSaved: (itemId) =>
                setCheckoutError((prev) =>
                    prev?.kind === "outOfStock"
                        ? withoutShortfallLine(prev, itemId)
                        : prev,
                ),
        });
    const isCartMutating = useAtomValue(isProcessingShoppingCartAtom);
    const { getLocations } = useLocationApi();
    const { createOrder } = useOrderApi();
    const { calculateShipping } = useShippingApi();
    const { getSurchargeQuote } = usePaymentQuoteApi();
    const { showError } = useNotification();

    const paymentRef = useRef<PaymentFormHandle>(null);
    // Guards the WHOLE submit sequence (getPaymentToken + createOrder), not just createOrder — the
    // ref is synchronous so a double-click before isProcessingOrderAtom flips (it is set only
    // inside createOrder) can't fire a second charge with the same nonce. isSubmitting mirrors it
    // for the button's disabled state.
    const isSubmittingRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [shoppingCart, setShoppingCart] = useAtom(shoppingCartAtom);
    // The status of the cart read THIS page issued (#407), never app-wide
    // atoms: those outlive the page, so with no cart in hand a revisit painted
    // the last visit's failure, and a first visit painted the empty checkout
    // before the cart had been read. The read is issued on mount, on Retry and
    // again when it changes: a new destination (or session). Until it answers,
    // a cart in hand may be the previous destination's (isRestricted flags and
    // all), so it may stay on screen but Place Order waits for the read; and if
    // the read fails the page says so rather than leave that cart up (PR 374
    // round 1b). Wrapped so the `isCurrent` useMountRead hands a read never
    // reaches getShoppingCart as its options.
    const readCart = useCallback(() => getShoppingCart(), [getShoppingCart]);
    const { readStatus: cartReadStatus, reload: fetchCart } =
        useMountRead(readCart);
    const isReadingCart = cartReadStatus === "loading";
    const locations = useAtomValue(locationsAtom);
    const activeShipToLocation = useAtomValue(activeShipToLocationAtom);
    const eligibleShipToLocations = useAtomValue(eligibleShipToLocationsAtom);
    const browsesUnfiltered = useAtomValue(browsesUnfilteredAtom);
    // The status of the GET /locations THIS page issued, never the shared
    // location atoms: an earlier failed read (the switcher's, or a previous
    // visit's) left them "failed" until the next read started, after the first
    // paint, so the shipping card said the locations couldn't be loaded before
    // this page had asked (PR 374 review 1). Secondary lookup: a failure must
    // not blank the page. useApi toasts it; with no list in hand the shipping
    // card also says so and offers `loadLocations` as Retry (PR 367 review,
    // finding 4).
    const { readStatus: locationsStatus, reload: loadLocations } =
        useMountRead(getLocations);
    const setStoredShipToLocationId = useSetAtom(storedShipToLocationIdAtom);
    const isPlacing = useAtomValue(isProcessingOrderAtom);
    const shippingPolicy = useAtomValue(shippingPolicyAtom);
    // #417. Same keying as the Cart: the payload says only THAT a line is
    // restricted, so a member seated on no location — whose legal-class lines
    // all fail closed and who has no location to pick — is recognised by the
    // status the API reports for exactly that member. The atom also yields to
    // this page's own fresh GET /locations (above): once an eligible location
    // comes back, the status is stale and the normal location UI takes over.
    const hasNoLocationMembership = useAtomValue(hasNoLocationMembershipAtom);

    const [isLocationModalOpen, setLocationModalOpen] = useState(false);
    // Server-computed shipping cost. null = not yet fetched (e.g. no location selected yet
    // or the fetch is in flight); falls back to the policy atom's fee while pending.
    const [serverShippingCost, setServerShippingCost] = useState<number | null>(null);
    // Inline failure state (#156) — one of the contracted POST /orders failure
    // outcomes. Cleared on a new submit attempt and on a location change.
    // The do-not-retry block derived from it is DELIBERATELY view-state only (Lead
    // ruling 2026-08-21): the discarded single-use nonce is the hard guard against a
    // double charge, and server-side idempotency is #348's documented future
    // hardening. Reload and location change are the block's supported escapes;
    // single-location orgs recover via support.
    const [checkoutError, setCheckoutError] = useState<CheckoutOrderError | null>(null);
    // The payment method the shopper has selected inside PaymentForm (#297). Card is
    // PaymentForm's own default; onMethodChange keeps the two in lockstep so the fee
    // line always tracks the chosen method BEFORE Place Order (§1.11).
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>(
        PaymentMethodType.Card,
    );
    // The server's surcharge quote for the currently displayed amount. "failed" is a
    // TERMINAL state distinct from "loading": the fee gate holds Place Order for both, but
    // only one of them is actually calculating, and only one needs a retry gesture. Folding
    // them into a single `null` left a failed quote sitting behind "Calculating card
    // processing fee…" forever, with no way out but a page reload.
    const [surchargeQuote, setSurchargeQuote] =
        useState<SurchargeQuoteState>(IDLE_QUOTE);
    // Bumped by the Retry control to re-run the quote effect with identical inputs.
    const [quoteAttempt, setQuoteAttempt] = useState(0);
    // Whether a bank capture is in flight inside PaymentForm (#296) — see its
    // onCaptureInFlightChange prop.
    const [isCapturingPayment, setIsCapturingPayment] = useState(false);
    // Which (location, subtotal) the shipping fetch has finished for — resolved OR failed.
    // A KEY rather than an "is fetching" flag on purpose: the fee must be quoted against a
    // SETTLED amount (the quote is a pure calculation and routinely beat /shipping home, so
    // quoting early priced the policy-fee fallback and then re-priced the real one), and a
    // boolean set from inside the shipping effect flips too late — the quote effect in the
    // same commit still sees the previous render's value and fires anyway. Comparing the key
    // to the current inputs is derived state, so it is already false on the render where the
    // inputs changed.
    const [settledShippingKey, setSettledShippingKey] = useState<string | null>(
        null,
    );

    const items = useMemo(() => shoppingCart?.items ?? [], [shoppingCart]);

    // The stock-changed 409 (PR 339): the stock moved during the attempt, so the stock and
    // the cart are read again, in parallel, and the next attempt is gated on what they are
    // now. Bumping `stockReadAttempt` re-reads every product on screen at once (the 409
    // keeps the cart as it was); a product the re-read cart brings is read when it arrives.
    // While either read is out Place Order waits; if either fails the alert says so and
    // Place Order is left to the server, which refuses again whatever the stock can't cover.
    const [stockReadAttempt, setStockReadAttempt] = useState(0);
    const [cartReread, setCartReread] = useState<"idle" | "reading" | "failed">(
        "idle",
    );

    // TODO(backend): /shoppingcarts payload has no image URL and no vendor stock
    // (#443) — hydrate by fetching each product, through the hook the Cart uses.
    const {
        products,
        status: productsStatus,
        failed: failedProductReads,
        retryFailed: retryFailedProductReads,
    } = useCartProducts(
        items.map((item) => item.productId),
        stockReadAttempt,
    );
    const isRefreshingStock =
        stockReadAttempt > 0 &&
        (productsStatus === "reading" || cartReread === "reading");
    // "May be out of date" is said of figures still on screen from an older read. A product
    // that never loaded shows none, and says so on its own line (with a Retry) — one
    // failure, one message (PR 356 review).
    const stockMayBeStale =
        cartReread === "failed" ||
        [...failedProductReads].some((id) => products.has(id));

    const refreshCartAndStock = () => {
        setStockReadAttempt((attempt) => attempt + 1);
        setCartReread("reading");
        // useApi toasts a failed cart read. A read that answers no cart is no fresher.
        getShoppingCart().then(
            (cart) => setCartReread(cart ? "idle" : "failed"),
            () => setCartReread("failed"),
        );
    };

    // #228 / E4-S12 (Lead ruling), and the SAME helper the cart uses — the two figures may
    // never disagree. Restricted lines are out of the subtotal, so they are also out of the
    // shipping quote, the card-fee quote and the order total below.
    //
    // Every one of those numbers therefore describes what the shopper would pay once the
    // restricted lines are gone, NOT what is about to be charged: CreateOrderCommand answers
    // 412 naming each restricted product and refuses the WHOLE order. That is why this page
    // renders RestrictedLinesNotice — a re-priced "Order total" beside a live Place Order
    // button and no explanation would read as "we'll skip those and charge you for the rest".
    //
    // PO ruling 2026-11-02: a line no live vendor offers is left out the same way, with
    // the same helper the Cart uses — its price is the dead offer's (offeredLines).
    const pricedItems = offeredLines(items, products);
    const subtotal = shippableSubtotal(pricedItems);
    const restrictedCount = restrictedLineCount(items);

    const lineCount = items.length;
    // The count beside the subtotal counts what the subtotal prices — one
    // denominator, not units over all lines against money over the shippable
    // ones.
    const itemCount = shippableUnitCount(pricedItems);

    // THE destination — the active ship-to one, with no checkout-local copy beside it.
    //
    // This page used to hold its own `selectedLocationId` that the modal wrote and nothing else
    // read: `createOrder` sent it while `item.isRestricted`, `shippableSubtotal` and
    // `restrictedLineCount` all came from what the API computed for the HEADER's destination. A
    // shopper whose cart read clean for IL could pick a CA location here, watch shipping and the
    // card fee re-quote for CA, and meet a 412 naming products this page never flagged — after
    // the card had been tokenized. Two destinations, one order (#228 / E4-S12).
    //
    // Selecting here therefore changes the ship-to context itself: the modal writes
    // storedShipToLocationIdAtom, which re-keys callShipToApi, which re-issues the cart read
    // (useShoppingCartApi.getShoppingCart has it in its deps) with fresh isRestricted flags. The
    // notice and the re-priced totals arrive BEFORE Place Order, not after tokenization.
    //
    // ONE destination, the same one every read on this page is answered for: the active
    // ship-to location already falls back to the row the API flags as its default when no id
    // is sent (that is what the API resolves a request with no id to, #404). For a customer
    // with any eligible location there is always such a row, so the tail fires only for an
    // admin: an admin's reads are unfiltered and no row is flagged, but an order still needs a
    // location. The tail is the ELIGIBLE list — never the raw one, which carries Pending and
    // Rejected locations that the catalogue, the restriction flags and the shipping quote would
    // then disagree with. A shopper with no eligible location has no destination and cannot
    // order; disabledReason says why.
    const effectiveLocationId =
        activeShipToLocation?.id ?? eligibleShipToLocations[0]?.id;
    // A checkout error, and with it a do-not-retry block, lifts when the location the order
    // would go to ACTUALLY changes — new location, new attempt context — whichever surface
    // changed it: the picker below or the header's switcher. Never on a pick alone: re-picking
    // the selected row changes nothing, and an admin's pick is stored but never sent. Adjusted
    // during render (React's pattern for state that follows a value), so no paint shows the old
    // location's error beside the new location.
    const [errorLocationId, setErrorLocationId] = useState(effectiveLocationId);
    if (errorLocationId !== effectiveLocationId) {
        setErrorLocationId(effectiveLocationId);
        setCheckoutError(null);
    }
    const selectedLocation = locations.find(
        (location) => location.id === effectiveLocationId,
    );
    // With no destination, why — the four are told apart so the page never says
    // "select a location" with nothing to select (PR 367 review, finding 4).
    // #403: HAS locations, none can receive orders. With an empty list, the
    // read's status decides: failed, loaded empty, or still loading.
    const locationGap: ShippingLocationGap | undefined = effectiveLocationId
        ? undefined
        : locations.length > 0
            ? "noneEligible"
            : locationsStatus === "failed"
                ? "loadFailed"
                : locationsStatus === "loaded"
                    ? "noLocations"
                    : "loading";

    // Fetch the authoritative shipping cost from the API whenever the location or
    // cart subtotal changes. The server applies the free-shipping threshold rule so
    // the UI never needs to replicate that logic. The `cancelled` guard drops any
    // stale response when a later change supersedes the in-flight request.
    // Identifies the shipping fetch by exactly the inputs that determine its answer.
    const shippingKey = `${effectiveLocationId ?? ""}|${subtotal}`;
    useEffect(() => {
        if (!effectiveLocationId) {
            setServerShippingCost(null);
            setSettledShippingKey(shippingKey);
            return;
        }
        let cancelled = false;
        calculateShipping(effectiveLocationId, subtotal)
            .then((response) => {
                if (!cancelled) setServerShippingCost(response.shippingCost);
            })
            .catch(() => {
                /* fall back to shippingPolicy.belowThresholdFee — see comment below */
            })
            .finally(() => {
                // "Settled" means the fetch is OVER, resolved or failed — not that a server
                // value arrived. On failure the displayed amount is the policy-fee estimate
                // and the fee is quoted against that, exactly as before: blocking the fee
                // (and with it all card checkout) on a shipping outage would be a far worse
                // trade than disclosing a fee computed on the same estimate the shopper is
                // looking at. Left alone when cancelled — a newer request owns the key.
                if (!cancelled) setSettledShippingKey(shippingKey);
            });
        return () => {
            cancelled = true;
        };
    }, [effectiveLocationId, subtotal, shippingKey, calculateShipping]);

    // Only the SERVER value is authoritative (order.total is recomputed server-side
    // at CreateOrder time, so the charge is always correct). The policy fee fallback
    // exists to avoid rendering an empty/misleading zero during the initial fetch or
    // on transient failure — the actual amount charged may still differ (e.g. free
    // shipping when subtotal >= FreeThreshold). Do not rely on this value for any
    // client-side gating.
    const shippingAndHandling = serverShippingCost ?? shippingPolicy.belowThresholdFee;

    // #297 (§1.11): the card processing fee is quoted by the SERVER for the displayed
    // amount — the UI never computes amount × rate. The SAME predicate useAcceptUi and
    // useAcceptJs gate on (a hand-copy here could drift into PaymentForm rendering "checkout
    // is unavailable" while this page still waited on a quote). An unconfigured
    // environment cannot take a payment, so it places NO order (#429): Place Order is
    // disabled below, PaymentForm says checkout is unavailable, and no quote is requested.
    const isPaymentConfigured = isCardPaymentConfigured();
    // Fee applicability is DERIVED from the selected method, not from lingering quote
    // state, so toggling to ACH removes the fee from the rendered totals synchronously
    // — before the cancelled fetch settles.
    const surchargeApplies =
        isPaymentConfigured && paymentMethod === PaymentMethodType.Card;

    // The amount the fee is quoted against — and the same figure the summary's own rows add
    // up to, derived once so the two can't disagree.
    const quotedAmount = subtotal + shippingAndHandling;
    // A fee is only owed on a positive amount: the server binds `amount` as a strict decimal
    // and 400s zero/negative, and there is nothing to disclose on a $0 cart. Distinct from
    // `surchargeApplies` because this also decides whether the fee ROW renders — a zero-priced
    // cart shows no fee line and, crucially, is not gated on a quote it can never obtain.
    const surchargeIsOwed = surchargeApplies && quotedAmount > 0;

    // Fetch the surcharge quote whenever the DISPLAYED amount changes while Card is the
    // selected method. The old quote is dropped up front — it priced the old amount and must
    // never render against the new one — and the same `cancelled` guard as the shipping
    // effect drops out-of-order responses. ACH and unconfigured environments never request a
    // quote; toggling back to Card re-fetches (deliberately no cache: the amount may have
    // changed while the fee was irrelevant). Waits for shipping to settle so the fee is
    // priced once, on the authoritative amount.
    useEffect(() => {
        if (!surchargeIsOwed || items.length === 0 || !effectiveLocationId) {
            setSurchargeQuote(IDLE_QUOTE);
            return;
        }
        if (settledShippingKey !== shippingKey) {
            // Shipping for THIS amount hasn't come back yet — hold at "loading" rather than
            // quoting a figure that is about to change.
            setSurchargeQuote({ status: "loading" });
            return;
        }
        let cancelled = false;
        setSurchargeQuote({ status: "loading" });
        getSurchargeQuote(quotedAmount)
            .then((quote) => {
                if (!cancelled) setSurchargeQuote({ status: "ready", quote });
            })
            .catch(() => {
                // Terminal, not silent: Place Order stays gated (§1.11 — no undisclosed
                // fee) but the summary now says the calculation failed and offers Retry,
                // instead of claiming to still be calculating forever.
                if (!cancelled) setSurchargeQuote({ status: "failed" });
            });
        return () => {
            cancelled = true;
        };
    }, [
        surchargeIsOwed,
        quotedAmount,
        items.length,
        effectiveLocationId,
        settledShippingKey,
        shippingKey,
        quoteAttempt,
        getSurchargeQuote,
    ]);

    const orderTotal =
        quotedAmount +
        (surchargeQuote.status === "ready"
            ? surchargeQuote.quote.surchargeAmount
            : 0);

    const locationApproved = isLocationApproved(selectedLocation);
    // A do-not-retry payment failure (unconfirmed/held/orderSaveFailed) blocks
    // further attempts until the page is reloaded or the location changes —
    // another attempt risks a double charge. A decline, the 409s and the stock 412
    // took no payment and stay retryable.
    const blockedByPaymentFailure =
        checkoutError !== null && isDoNotRetry(checkoutError.kind);
    // §1.11: the card fee must be DISCLOSED before the order can be placed — while Card is
    // selected and no server quote has landed, Place Order waits. A zero-amount cart owes no
    // fee and so is never gated (it has nothing to disclose and the server would 400 the
    // quote); ACH and unconfigured paths are unaffected.
    const awaitingSurchargeQuote =
        surchargeIsOwed && surchargeQuote.status !== "ready";
    // A restricted line refuses the WHOLE order server-side (412), so the client refuses it
    // first — before the card is tokenized for an order it knows will be turned away. A
    // pending or in-flight cart edit gates too: the order would be placed against the
    // quantity the server still holds, not the one on screen.
    const hasRestrictedLines = restrictedCount > 0;
    // #443 / C-07: a line above its vendor's known stock refuses the whole order (412)
    // the same way, so it gates the same way — from the product reads the Cart uses,
    // except for the lines a stock 412 marked: checkout's own reading of their stock is
    // newer, so it wins (each such row is also marked with it below).
    const shortfall = new Map(
        (checkoutError?.kind === "outOfStock" ? checkoutError.items ?? [] : []).map(
            (line) => [line.shoppingCartItemId, line.available],
        ),
    );
    const { stocks, overStockCount } = cartStock(items, products, shortfall);
    const hasOverStockLines = overStockCount > 0;
    // PO ruling 2026-11-02: a line no live vendor offers can't be ordered — read
    // from the same product reads, and gated the same way.
    const noOfferCount = noOfferLineCount(items, products);
    const hasNoOfferLines = noOfferCount > 0;
    const isCartSettling = hasPendingEdits || isCartMutating;
    const canPlaceOrder =
        isPaymentConfigured &&
        !!effectiveLocationId &&
        locationApproved &&
        items.length > 0 &&
        !hasRestrictedLines &&
        !hasOverStockLines &&
        !hasNoOfferLines &&
        !isCartSettling &&
        !isReadingCart &&
        !isRefreshingStock &&
        !isPlacing &&
        !isSubmitting &&
        !isCapturingPayment &&
        !blockedByPaymentFailure &&
        !awaitingSurchargeQuote;
    // The no-location rung sits ABOVE the location and restricted-line rungs on
    // purpose (#417): a member seated on no location has no location to select
    // and no "different location" to ship to, so both of those sentences would
    // be advice they cannot follow. Their way out is USNara support. It does not
    // read restrictedCount: this member can place no order at all, so the
    // reason names the order, never the cart's lines.
    // Payment-unavailable sits above every other rung (#429): no location, cart or
    // fee fix can make an order placeable while there is nothing to pay with.
    const disabledReason =
        items.length === 0
            ? "Your cart is empty"
            : !isPaymentConfigured
                ? CHECKOUT_UNAVAILABLE_REASON
                : hasNoLocationMembership
                    ? NO_LOCATION_ORDER_BLOCKED
                    : !effectiveLocationId
                        ? SHIPPING_LOCATION_GAP_COPY[locationGap ?? "loading"]
                        : !locationApproved
                            ? "This location isn't approved for ordering yet"
                            : hasRestrictedLines
                                ? "Remove the items that can't ship to your selected location"
                                : hasNoOfferLines
                                    ? outOfStockReason(noOfferCount)
                                    : hasOverStockLines
                                        ? overStockReason(overStockCount)
                                        : isCartSettling
                                            ? "Saving your cart changes…"
                                            : isReadingCart
                                                ? "Checking your cart for this location…"
                                                : isRefreshingStock
                                                    ? "Checking the latest stock…"
                                                    : blockedByPaymentFailure
                                                        ? "Contact support before trying again"
                                                        : isCapturingPayment
                                                            ? "Verifying your bank details…"
                                                            : surchargeQuote.status === "failed"
                                                                ? "We couldn't calculate the card processing fee"
                                                                : awaitingSurchargeQuote
                                                                    ? "Calculating card processing fee…"
                                                                    : undefined;

    const handlePlaceOrder = async () => {
        if (!effectiveLocationId || items.length === 0) return;
        // Defense in depth beyond the disabled attribute: both money-relevant gates
        // must hold even if a submit reaches the handler some other way. A
        // do-not-retry block, because another attempt risks a double charge; the
        // fee gate, because §1.11 forbids placing a card order whose processing fee
        // was never disclosed.
        if (blockedByPaymentFailure || awaitingSurchargeQuote) return;
        // The cart gates hold here too: none may reach the payment vendor, let
        // alone createOrder.
        if (
            hasRestrictedLines ||
            hasOverStockLines ||
            hasNoOfferLines ||
            isCartSettling ||
            isReadingCart ||
            isRefreshingStock
        ) {
            return;
        }
        // #429: no payment keys means nothing to pay with — never an unpaid order.
        // The button is already disabled with CHECKOUT_UNAVAILABLE_REASON beside it,
        // so a submit that gets here anyway has nothing new to say.
        if (!isPaymentConfigured) return;
        // PaymentForm renders beside the button, so its handle is always attached
        // when the button can be clicked. If that ever stops holding, there is still
        // nothing to pay with — and the click must say so, never do nothing.
        const paymentForm = paymentRef.current;
        if (!paymentForm) {
            showError({
                title: CHECKOUT_UNAVAILABLE_TITLE,
                message: CHECKOUT_UNAVAILABLE_MESSAGE,
            });
            return;
        }
        // Synchronous re-entrancy guard: blocks a second submit fired before isProcessingOrderAtom
        // (set only inside createOrder) ever flips. Without this, a double-click could attempt to
        // charge twice for one click intent.
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        // A fresh attempt invalidates the previous failure state. For declined this
        // is the invited retry; the do-not-retry kinds disable the button, so they
        // never reach here.
        setCheckoutError(null);

        try {
            // The payment was already captured/picked inside PaymentForm — raw card data
            // lives only in Authorize.Net's hosted lightbox, and raw bank details only in the
            // form + the Accept.js call; neither ever reaches this page. getPaymentSelection
            // hands back the stored selection, and never resolves without one (#429):
            // nothing captured/picked yet for the selected method → it throws, aborting
            // before any order is created.
            let selection: PaymentSelection;
            try {
                selection = await paymentForm.getPaymentSelection();
            } catch (error) {
                showError({
                    title: "Payment details",
                    message:
                        error instanceof Error
                            ? error.message
                            : "We couldn't verify your payment details. Please check them and try again.",
                });
                return;
            }

            try {
                // createOrder suppresses useApi's toast for EXACTLY the
                // contracted outcomes rendered inline below (the four payment
                // outcomes, the 409s and the stock 412); every other failure (400
                // problem+json/validation, any other 412, unknown 402 codes,
                // bodyless 5xx, network) already toasted with full fidelity
                // through useApi's handlers (#156).
                //
                // Every request carries paymentMethod and exactly one credential — a
                // nonce or a saved profile — by PaymentSelection's shape and
                // CreateOrderRequest's: never neither (#429; the server 400s a request
                // with no instrument) and never both (also a 400). A 400 is a failure
                // like any other non-contract one: useApi toasts it and the catch
                // below resets the nonce; nothing navigates.
                const { id } = await createOrder(
                    "vendorPaymentToken" in selection
                        ? {
                            locationId: effectiveLocationId,
                            paymentMethod: selection.method,
                            vendorPaymentToken: selection.vendorPaymentToken,
                        }
                        : {
                            locationId: effectiveLocationId,
                            paymentMethod: selection.method,
                            paymentProfileId: selection.paymentProfileId,
                        },
                );
                // CreateOrder clears the cart server-side; clear it locally so the
                // header badge resets immediately. Any quantity edit still armed
                // is DROPPED, not flushed: the unmount flush would PATCH items
                // that no longer exist and toast a 404 over the confirmation.
                discardPending();
                setShoppingCart((prev) => (prev ? { ...prev, items: [] } : prev));
                navigate(ROUTES.ORDER_CONFIRMATION(id));
            } catch (error) {
                const failure = checkoutOrderError(error);
                if (failure) {
                    // One of the contracted outcomes — inline, no toast.
                    setCheckoutError(failure);
                    if (isDoNotRetry(failure.kind)) {
                        // The server kept the cart's checkout claim, so its lines are
                        // gone (#441). Mirror that here — the header badge, and no line
                        // left whose stepper would PATCH an item that no longer exists —
                        // while the alert, rendered on the empty view too, says why.
                        discardPending();
                        setShoppingCart((prev) =>
                            prev ? { ...prev, items: [] } : prev,
                        );
                    }
                    if (failure.kind === "stockChanged") {
                        refreshCartAndStock();
                    }
                }
                // The Accept nonce is SINGLE-USE and this attempt consumed it — discard
                // any stored capture (card AND bank alike) so the user re-enters before
                // retrying. A saved-account pick survives inside PaymentForm: it is a
                // reusable DB id, not a nonce. Applies to ALL failures, inline or toasted.
                paymentRef.current?.resetPayment();
            }
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    if (cartReadStatus === "loading" && !shoppingCart) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <Spinner size="lg" />
            </div>
        );
    }

    // Whether or not a cart is in hand: see cartReadStatus above.
    if (cartReadStatus === "failed") {
        return (
            <div className="bg-base-200 text-neutral min-h-[60vh]">
                <div className="max-w-6xl mx-auto px-4 py-4 md:py-6">
                    <ErrorState onRetry={fetchCart} />
                </div>
            </div>
        );
    }

    // Explicit text-error-content: the light theme sets --color-base-content to
    // white (see the CLAUDE.md theme quirk), so never let the alert inherit a
    // base-content foreground. One element, rendered in both views: a
    // do-not-retry failure empties the cart, and the alert must outlive it.
    const checkoutErrorAlert = checkoutError && (
        <div
            role="alert"
            data-testid="checkout-error"
            className="alert alert-error text-error-content"
        >
            <Icon name="error" filled />
            <div>
                <h3 className="font-bold">
                    {CHECKOUT_ERROR_TITLES[checkoutError.kind]}
                </h3>
                <p>{checkoutError.message}</p>
                {isDoNotRetry(checkoutError.kind) && <p>{CART_EMPTIED_NOTE}</p>}
                {checkoutError.kind === "stockChanged" && stockMayBeStale && (
                    <p>{STALE_STOCK_NOTE}</p>
                )}
                {checkoutError.kind === "conflict" && (
                    <Link
                        to={ROUTES.ACCOUNT_ORDERS}
                        className="underline font-semibold"
                    >
                        View your orders
                    </Link>
                )}
                {checkoutError.kind === "outOfStock" && (
                    <Link to={ROUTES.CART} className="underline font-semibold">
                        Back to Cart
                    </Link>
                )}
            </div>
        </div>
    );

    return (
        <div className="bg-base-200 text-neutral min-h-[60vh]">
            <div className="max-w-6xl mx-auto px-4 py-4 md:py-6 text-left">
                <nav className="text-sm text-neutral/60 mb-2" aria-label="Breadcrumb">
                    <Link to={ROUTES.CART} className="hover:underline">
                        Cart
                    </Link>
                    <span className="mx-1">›</span>
                    <span className="text-neutral">Checkout</span>
                </nav>

                <header className="mb-4 flex items-center gap-2">
                    <Icon name="lock" className="text-2xl text-neutral" />
                    <h1 className="text-2xl md:text-3xl font-bold text-neutral">
                        Checkout
                    </h1>
                </header>

                <div className="bg-base-100 rounded-lg p-4 md:p-5 mb-4 md:mb-6">
                    <CheckoutSteps current={2} />
                </div>

                {items.length === 0 ? (
                    <>
                        {checkoutErrorAlert && (
                            <div className="mb-4">{checkoutErrorAlert}</div>
                        )}
                        <div className="bg-base-100 rounded-lg p-6 text-center">
                            <p className="text-neutral/70">Your cart is empty.</p>
                            <Link
                                to={ROUTES.CART}
                                className="btn btn-outline mt-4 text-neutral border-neutral hover:bg-base-200"
                            >
                                Back to Cart
                            </Link>
                        </div>
                    </>
                ) : (
                    <div className="grid gap-4 lg:gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
                        <div className="lg:order-1 flex flex-col gap-4">
                            <ShippingCard
                                location={selectedLocation}
                                // The picker offers only eligible locations;
                                // with none, Change would open onto an empty
                                // list (#403), so there is no Change. Nor for
                                // an admin (#404): their reads send no id, so a
                                // pick would be stored and change nothing.
                                onChange={
                                    eligibleShipToLocations.length > 0 &&
                                    !browsesUnfiltered
                                        ? () => setLocationModalOpen(true)
                                        : undefined
                                }
                                locationGap={locationGap}
                                onRetryLocations={loadLocations}
                            />

                            {/* Same swap as the Cart (#417): the destination banner's
                                "Change location" opens onto an empty picker for a
                                member on no location. The note shows with nothing
                                restricted too — this member can place no order at
                                all, and the page should say so, not only the line
                                under a disabled Place Order. */}
                            {hasNoLocationMembership ? (
                                <NoLocationNote
                                    message={
                                        restrictedCount > 0
                                            ? NO_LOCATION_CART_NOTE
                                            : NO_LOCATION_ORDER_BLOCKED
                                    }
                                />
                            ) : (
                                <RestrictedLinesNotice count={restrictedCount} />
                            )}

                            {checkoutErrorAlert}

                            <PaymentForm
                                ref={paymentRef}
                                onMethodChange={setPaymentMethod}
                                onCaptureInFlightChange={setIsCapturingPayment}
                            />

                            <section className="bg-base-100 rounded-lg p-4 md:p-5">
                                <h2 className="font-bold text-lg text-neutral mb-2">
                                    Your Items ({lineCount})
                                </h2>
                                <div>
                                    {items.map((item, index) => {
                                        const product = products.get(
                                            item.productId,
                                        );
                                        const stock = stocks[index];
                                        const marked = shortfall.get(item.id);
                                        return (
                                            <CartItemRow
                                                key={item.id || item.vendorSkuId}
                                                item={item}
                                                imageUrl={
                                                    product &&
                                                    getProductImage(product)
                                                }
                                                detailsFailed={
                                                    !product &&
                                                    failedProductReads.has(
                                                        item.productId,
                                                    )
                                                }
                                                onRetryDetails={
                                                    retryFailedProductReads
                                                }
                                                piecesPerUnit={linePiecesPerUnit(
                                                    item,
                                                    product,
                                                )}
                                                showRemove={false}
                                                // #443: the same cap and stock line
                                                // as the Cart (floor 1, at most the
                                                // per-line limit); the vendor switch
                                                // stays on the Cart.
                                                maxQuantity={quantityCap(
                                                    stock?.available,
                                                )}
                                                outOfStock={lineHasNoOffers(
                                                    item,
                                                    product,
                                                )}
                                                notice={
                                                    marked !== undefined ? (
                                                        // A line the stock 412 named,
                                                        // with the stock it stated —
                                                        // shown even within it: lines of
                                                        // one offering share its stock.
                                                        <p
                                                            role="alert"
                                                            data-testid="cart-item-shortfall"
                                                            className="text-sm font-semibold text-error"
                                                        >
                                                            {stockLimitMessage(marked)}
                                                        </p>
                                                    ) : (
                                                        <LineStockNotice
                                                            quantity={item.quantity}
                                                            stock={stock}
                                                        />
                                                    )
                                                }
                                                quantityRefusal={
                                                    quantityRefusals[item.id]?.message
                                                }
                                                onQuantityChange={(next) =>
                                                    changeQuantity(item, next)
                                                }
                                                onRemove={() => {}}
                                            />
                                        );
                                    })}
                                </div>
                            </section>
                        </div>

                        <aside className="lg:order-2 lg:sticky lg:top-4 self-start">
                            <CheckoutSummary
                                itemCount={itemCount}
                                subtotal={subtotal}
                                excludedCount={restrictedCount}
                                shippingAndHandling={shippingAndHandling}
                                orderTotal={orderTotal}
                                canPlaceOrder={canPlaceOrder}
                                isPlacing={isPlacing || isSubmitting}
                                onPlaceOrder={handlePlaceOrder}
                                disabledReason={disabledReason}
                                showSurcharge={surchargeIsOwed}
                                surchargeQuote={
                                    surchargeQuote.status === "ready"
                                        ? surchargeQuote.quote
                                        : null
                                }
                                surchargeQuoteFailed={
                                    surchargeQuote.status === "failed"
                                }
                                onRetrySurchargeQuote={() =>
                                    setQuoteAttempt((attempt) => attempt + 1)
                                }
                            />
                        </aside>
                    </div>
                )}
            </div>

            {isLocationModalOpen && (
                <LocationSelectModal
                    // The ELIGIBLE list, not the raw one. The stored destination is only
                    // honoured while it is eligible, so offering a Pending or Rejected location
                    // here would let the modal set a destination the ship-to context cannot
                    // hold — the two destinations would be one in name only.
                    locations={eligibleShipToLocations}
                    selectedLocationId={effectiveLocationId}
                    onSelect={(locationId) => {
                        // The modal fires onSelect even for the row that is
                        // already selected. The checkout error is cleared by
                        // errorLocationId above, only when the effective
                        // location actually changes.
                        setStoredShipToLocationId(locationId);
                    }}
                    onClose={() => setLocationModalOpen(false)}
                />
            )}
        </div>
    );
};
